import { strFromU8, strToU8 } from 'fflate';
import { z } from 'zod';
import type { Id } from '@vp/shared-types';
import type { StorageAdapter } from './storage/adapter';

/**
 * Parts of a canvas that live beside its objects and links, outside transactions: the data modules
 * keep for the canvas now, the data sets of tables and charts later (docs/TZ-DATA.md). Whatever moves a
 * canvas — a duplicate, the move into a folder, an archive, a profile file — goes through this list, so a
 * new part is added in one place and is never lost on the way. Deleting a canvas deletes its parts in the
 * storage adapter itself (in the same transaction as the canvas).
 */
export interface CanvasPart<T = unknown> {
  /** Name of the part in archives: `parts/<id>/…`. Stable once released. */
  readonly id: string;
  /** What the canvas has, or null when there is nothing. */
  read(storage: StorageAdapter, canvasId: Id): Promise<T | null>;
  /** Put it into a canvas (a copy or an import: the canvas id is new). */
  write(storage: StorageAdapter, canvasId: Id, value: T): Promise<void>;
  /** Files for an archive, by name inside the part's folder. */
  toFiles(value: T): Promise<Record<string, Uint8Array>> | Record<string, Uint8Array>;
  /** Back from the archive's files; throws a readable message on a damaged or wrong part. */
  fromFiles(files: Record<string, Uint8Array>): Promise<T> | T;
}

const parts = new Map<string, CanvasPart>();

export function registerCanvasPart<T>(part: CanvasPart<T>): void {
  if (!/^[a-z][a-zA-Z0-9-]{0,31}$/.test(part.id)) throw new Error(`invalid canvas part id ${part.id}`);
  if (parts.has(part.id)) throw new Error(`canvas part ${part.id} is registered twice`);
  parts.set(part.id, part as CanvasPart);
}

export function canvasParts(): CanvasPart[] {
  return [...parts.values()];
}

/** Every part of a canvas that has something, by part id. */
export async function readCanvasParts(storage: StorageAdapter, canvasId: Id): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  for (const p of parts.values()) {
    const v = await p.read(storage, canvasId);
    if (v !== null && v !== undefined) out[p.id] = v;
  }
  return out;
}

/** Write parts read before (a part this build does not know is skipped: an archive from a newer app). */
export async function writeCanvasParts(storage: StorageAdapter, canvasId: Id, values: Record<string, unknown>): Promise<void> {
  for (const [id, value] of Object.entries(values)) await parts.get(id)?.write(storage, canvasId, value);
}

/** Copy every part of one canvas into another one, in the same storage or another. */
export async function copyCanvasParts(from: StorageAdapter, fromId: Id, to: StorageAdapter, toId: Id): Promise<void> {
  await writeCanvasParts(to, toId, await readCanvasParts(from, fromId));
}

// ---- data modules keep for the canvas (API 2.2 `platform.storage.canvas`)

export interface ModuleDataEntry {
  moduleId: string;
  key: string;
  value: unknown;
}

export const ModuleDataFileSchema = z.array(z.object({ moduleId: z.string().max(128), key: z.string().max(200), value: z.unknown() })).max(100_000);

export const moduleDataPart: CanvasPart<ModuleDataEntry[]> = {
  id: 'moduleData',
  async read(storage, canvasId) {
    const rows = await storage.moduleData.ofScope(canvasId);
    return rows.length ? rows.map((r) => ({ moduleId: r.moduleId, key: r.key, value: r.value })) : null;
  },
  async write(storage, canvasId, value) {
    const now = Date.now();
    for (const r of value) {
      const json = JSON.stringify(r.value ?? null);
      await storage.moduleData.put({ moduleId: r.moduleId, scope: canvasId, key: r.key, value: r.value ?? null, size: json.length, updatedAt: now });
    }
  },
  toFiles: (value) => ({ 'data.json': strToU8(JSON.stringify(value)) }),
  fromFiles(files) {
    const f = files['data.json'];
    if (!f) throw new Error('data.json is missing');
    let json: unknown;
    try {
      json = JSON.parse(strFromU8(f));
    } catch {
      throw new Error('invalid JSON');
    }
    const r = ModuleDataFileSchema.safeParse(json);
    if (!r.success) throw new Error('invalid module data');
    return r.data.map((x) => ({ moduleId: x.moduleId, key: x.key, value: x.value }));
  },
};

registerCanvasPart(moduleDataPart);
